import React, { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import FeedbackSheet from './FeedbackSheet';

interface ScreenContext {
  name: string;
  hasFormData: boolean;
}

function getScreenContext(pathname: string): ScreenContext {
  // Match dynamic segments too
  if (pathname === '/setup' || pathname.startsWith('/setup/'))
    return { name: 'Setup', hasFormData: true };
  if (pathname === '/game' || pathname.startsWith('/game/'))
    return { name: 'Active Game', hasFormData: true };
  if (pathname.startsWith('/settlement/'))
    return { name: 'Settlement', hasFormData: true };
  if (pathname === '/library')
    return { name: 'Library', hasFormData: false };
  if (pathname.startsWith('/insights/'))
    return { name: 'Insights', hasFormData: false };
  if (pathname === '/settings')
    return { name: 'Settings', hasFormData: true };
  if (pathname === '/profile')
    return { name: 'Profile', hasFormData: true };
  if (pathname === '/admin/users' || pathname.startsWith('/admin/users/'))
    return { name: 'Admin Users', hasFormData: true };
  if (pathname === '/admin/feedback')
    return { name: 'Feedback', hasFormData: false };
  if (pathname === '/explorer')
    return { name: 'Data Explorer', hasFormData: false };
  if (pathname === '/entertainment')
    return { name: 'Entertainment', hasFormData: true };
  if (pathname === '/telegram-test')
    return { name: 'Bot Test', hasFormData: true };
  if (pathname === '/chip-scanner')
    return { name: 'Chip Scanner', hasFormData: true };
  return { name: 'App', hasFormData: false };
}

/** Detect if any input/textarea/select on the page has a non-empty value. */
function pageHasFormData(): boolean {
  if (typeof document === 'undefined') return false;
  const inputs = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
    'main input, main textarea, main select'
  );
  for (const el of inputs) {
    const val = el.value;
    if (val && val.trim().length > 0) return true;
  }
  return false;
}

interface FeedbackBubbleProps {
  isDrawerOpen?: boolean;
}

export default function FeedbackBubble({ isDrawerOpen }: FeedbackBubbleProps) {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const location = useLocation();

  const context = useMemo<ScreenContext>(() => {
    if (isDrawerOpen) return { name: 'Menu', hasFormData: false };
    const base = getScreenContext(location.pathname);
    if (!base.hasFormData) return base;
    return { ...base, hasFormData: pageHasFormData() };
  }, [location.pathname, isDrawerOpen]);

  const label = context.hasFormData
    ? `${context.name} · data input`
    : context.name;

  return (
    <>
      {/* Bubble with context label */}
      <div className="fixed z-[180] bottom-[72px] right-3 flex flex-col items-end gap-1">
        {/* Context label pill */}
        <div className="bg-white/90 backdrop-blur-sm text-[9px] font-bold text-aura-text/60 px-2 py-1 rounded-full shadow-sm border border-aura-text/10 whitespace-nowrap leading-none">
          {label}
        </div>

        {/* Bubble button */}
        <button
          onClick={() => setOpen(true)}
          className={`w-11 h-11 rounded-full bg-gradient-to-br from-aura-primary to-aura-secondary shadow-lg shadow-aura-primary/30 flex items-center justify-center text-white transition-all duration-300 active:scale-90 hover:shadow-xl hover:shadow-aura-primary/40 ${
            !dismissed ? 'animate-bounce-subtle' : ''
          }`}
          onMouseEnter={() => setDismissed(true)}
          title="Send feedback"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      </div>

      <FeedbackSheet
        isOpen={open}
        onClose={() => setOpen(false)}
        screenName={context.name}
        hasFormData={context.hasFormData}
      />

      <style>{`
        @keyframes bounce-subtle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        .animate-bounce-subtle {
          animation: bounce-subtle 2s ease-in-out infinite;
        }
      `}</style>
    </>
  );
}
